import React, { useState } from 'react';
import BasePOIModal from './BasePOIModal';
import { MeasurementSelector } from './components/MeasurementSelector';
import { NotesAndPhotos } from './components/NotesAndPhotos';
import { WidthClearance } from './components/WidthClearance';

const BRIDGE_TYPES = [
  { value: 'overpass', label: 'Overpass' },
  { value: 'underpass', label: 'Underpass' },
  { value: 'railroad', label: 'Railroad' },
  { value: 'pedestrian', label: 'Pedestrian' },
  { value: 'tunnel', label: 'Tunnel' }
];

const SURFACE_TYPES = [
  { value: 'concrete', label: 'Concrete' },
  { value: 'asphalt', label: 'Asphalt' },
  { value: 'steel', label: 'Steel' },
  { value: 'wood', label: 'Wood' }
];

export const BridgePOIModal = ({ type, poiInfo, onClose, onSave, onBack }) => {
  const [formData, setFormData] = useState({
    bridgeType: '',
    structureNumber: '',
    surfaceType: '',
    loadCapacity: '',
    hasWeightRestriction: false,
    touchLevel: null,
    measurementType: 'touch',
    customHeight: { feet: '', inches: '', meters: '' },
    width: { feet: '', inches: '', meters: '' },
    notes: '',
    photos: []
  });

  const handleSubmit = async () => {
    // Validate required fields
    const errors = {};
    if (!formData.bridgeType) errors.bridgeType = 'Bridge type is required';
    if (!formData.structureNumber) errors.structureNumber = 'Structure number is required';
    if (formData.measurementType === 'touch' && !formData.touchLevel) {
      errors.measurements = 'Touch level is required';
    }
    if (formData.measurementType === 'custom' && 
        !formData.customHeight.feet && 
        !formData.customHeight.meters) {
      errors.measurements = 'Height measurement is required';
    }

    if (Object.keys(errors).length > 0) {
      return { success: false, errors };
    }

    // Prepare measurements array
    const measurements = [];
    if (formData.measurementType === 'touch') {
      measurements.push({
        type: 'touch',
        value: formData.touchLevel,
        unit: 'inches'
      });
    } else {
      if (formData.customHeight.meters) {
        measurements.push({
          type: 'custom',
          value: parseFloat(formData.customHeight.meters),
          unit: 'meters'
        });
      } else {
        measurements.push({
          type: 'custom',
          value: parseInt(formData.customHeight.feet),
          unit: 'feet'
        });
        if (formData.customHeight.inches) {
          measurements.push({
            type: 'custom',
            value: parseInt(formData.customHeight.inches),
            unit: 'inches'
          });
        }
      }
    }

    return onSave({
      measurements,
      notes: formData.notes,
      photos: formData.photos,
      bridgeDetails: {
        type: formData.bridgeType,
        structureNumber: formData.structureNumber,
        surfaceType: formData.surfaceType,
        loadCapacity: formData.loadCapacity || null,
        hasWeightRestriction: formData.hasWeightRestriction
      }
    });
  };

  return (
    <BasePOIModal
      type={type}
      poiInfo={poiInfo}
      onClose={onClose}
      onSave={handleSubmit}
      onBack={onBack}
    >
      <div className="space-y-6">
        <MeasurementSelector
          measurementType={formData.measurementType}
          setMeasurementType={(type) => setFormData(prev => ({ ...prev, measurementType: type }))}
          touchLevel={formData.touchLevel}
          setTouchLevel={(level) => setFormData(prev => ({ ...prev, touchLevel: level }))}
          customHeight={formData.customHeight}
          setCustomHeight={(height) => setFormData(prev => ({ ...prev, customHeight: height }))}
        />

        <WidthClearance 
          width={formData.width}
          setWidth={(width) => setFormData(prev => ({ ...prev, width }))}
        />

        <div>
          <label className="block text-gray-400 mb-2">Bridge Type</label>
          <select
            value={formData.bridgeType}
            onChange={(e) => setFormData(prev => ({ ...prev, bridgeType: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">Select Bridge Type</option>
            {BRIDGE_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Structure Number</label>
          <input
            type="text"
            value={formData.structureNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, structureNumber: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            placeholder="Enter structure number"
          />
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Surface Type</label>
          <select
            value={formData.surfaceType}
            onChange={(e) => setFormData(prev => ({ ...prev, surfaceType: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">Select Surface Type</option>
            {SURFACE_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Load Capacity (tons)</label>
          <input
            type="number"
            value={formData.loadCapacity}
            onChange={(e) => setFormData(prev => ({ ...prev, loadCapacity: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            placeholder="Enter load capacity"
            min="0"
            step="0.1"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.hasWeightRestriction}
            onChange={(e) => setFormData(prev => ({ ...prev, hasWeightRestriction: e.target.checked }))}
            className="rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
          />
          <label className="text-gray-300">Has Weight Restriction</label>
        </div>

        <NotesAndPhotos
          notes={formData.notes}
          setNotes={(notes) => setFormData(prev => ({ ...prev, notes }))}
          photos={formData.photos}
          setPhotos={(photos) => setFormData(prev => ({ ...prev, photos }))}
        />
      </div>
    </BasePOIModal>
  );
};

export default BridgePOIModal;